import React, { useEffect, useState, useRef } from "react";
import { Globe, X, ChevronDown } from "lucide-react";

// Lightweight site-wide language switcher backed by Google Translate's free
// inline widget. Loads the script once on demand and exposes a custom-styled
// dropdown so we don't ship Google's default banner UI.
//
// Why this approach for Phase 1:
//   - Zero backend work, zero per-route page duplication
//   - Covers 100+ languages out of the box (incl. Spanish, French, German,
//     Italian, Portuguese, Dutch, Polish, Arabic, Hindi, Japanese, Korean,
//     Chinese, etc. — covers our top traffic origins)
//   - Easy to swap for hand-tuned country landing pages later without breaking
//     the URL surface (this only sets a `googtrans` cookie, doesn't change paths)
//
// Top-5 hand-tuned country landing pages remain a Phase 2 follow-up; this
// widget acts as the fallback for every other language right away.

const LANGS = [
  { code: "en", label: "English" },
  { code: "es", label: "Español" },
  { code: "fr", label: "Français" },
  { code: "de", label: "Deutsch" },
  { code: "it", label: "Italiano" },
  { code: "pt", label: "Português" },
  { code: "nl", label: "Nederlands" },
  { code: "pl", label: "Polski" },
  { code: "ar", label: "العربية" },
  { code: "hi", label: "हिन्दी" },
  { code: "ja", label: "日本語" },
  { code: "ko", label: "한국어" },
  { code: "zh-CN", label: "简体中文" },
  { code: "ru", label: "Русский" },
  { code: "tr", label: "Türkçe" },
];

const STORAGE_KEY = "motowear_lang";
let scriptInitialized = false;

function injectGoogleTranslate() {
  if (scriptInitialized) return;
  scriptInitialized = true;
  // Mount the hidden host element Google's widget needs
  if (!document.getElementById("google_translate_element")) {
    const el = document.createElement("div");
    el.id = "google_translate_element";
    el.style.cssText = "position:absolute;left:-9999px;top:-9999px;height:0;width:0;overflow:hidden;";
    document.body.appendChild(el);
  }
  window.googleTranslateElementInit = () => {
    // eslint-disable-next-line no-undef
    new google.translate.TranslateElement(
      {
        pageLanguage: "en",
        autoDisplay: false,
        includedLanguages: LANGS.map((l) => l.code).join(","),
        layout: window.google?.translate?.TranslateElement?.InlineLayout?.SIMPLE,
      },
      "google_translate_element",
    );
  };
  const s = document.createElement("script");
  s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  s.async = true;
  document.body.appendChild(s);
}

function setLang(code) {
  // Google Translate is driven entirely by the `googtrans` cookie. Setting it
  // on the bare hostname + on root path covers preview + production + future
  // subdomain microsites. We then ALWAYS reload — the runtime widget often
  // misses cookie changes mid-session, but a fresh navigation always picks
  // them up reliably across browsers (was the iter71+ bug report).
  const value = code === "en" ? "/en/en" : `/en/${code}`;
  const host = window.location.hostname;
  // Strip leading subdomain so cookie covers e.g. *.motowear.co.uk
  const rootHost = host.split(".").slice(-2).join(".");
  const oneYear = 60 * 60 * 24 * 365;
  // Clear any stale cookies first (Google requires a clean slate to switch)
  document.cookie = "googtrans=; path=/; max-age=0";
  document.cookie = `googtrans=; path=/; domain=.${rootHost}; max-age=0`;
  document.cookie = `googtrans=; path=/; domain=${host}; max-age=0`;
  // Now set the fresh one on every viable scope
  document.cookie = `googtrans=${value}; path=/; max-age=${oneYear}`;
  document.cookie = `googtrans=${value}; path=/; domain=.${rootHost}; max-age=${oneYear}`;
  document.cookie = `googtrans=${value}; path=/; domain=${host}; max-age=${oneYear}`;
  try { localStorage.setItem(STORAGE_KEY, code); } catch (_e) { /* ignore */ }
  // Reload — most reliable path. The select-change fallback used in iter69
  // was racy across Safari and the preview proxy.
  window.location.reload();
}

export default function LanguageSwitcher() {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("en");
  const ref = useRef(null);

  useEffect(() => {
    injectGoogleTranslate();
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setActive(saved);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const current = LANGS.find((l) => l.code === active) || LANGS[0];

  return (
    <div className="fixed bottom-5 left-5 z-40" ref={ref} data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Choose language"
        aria-expanded={open}
        data-testid="language-switcher-trigger"
        className="bg-black text-white hover:bg-[#FF3B30] transition-colors px-3 py-2 flex items-center gap-2 label-eyebrow text-[10px] shadow-lg"
      >
        <Globe size={14} strokeWidth={1.75} />
        <span className="notranslate">{current.label}</span>
        <ChevronDown size={12} strokeWidth={1.75} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          data-testid="language-switcher-menu"
          className="absolute bottom-full left-0 mb-2 bg-white border border-black/10 shadow-2xl py-2 min-w-[180px] max-h-[60vh] overflow-y-auto notranslate"
        >
          <div className="flex items-center justify-between px-3 py-1 mb-1">
            <span className="label-eyebrow text-[9px] text-black/40">Translate</span>
            <button
              onClick={() => setOpen(false)}
              aria-label="Close language menu"
              className="text-black/40 hover:text-black"
            >
              <X size={12} />
            </button>
          </div>
          {LANGS.map((l) => (
            <button
              key={l.code}
              role="menuitem"
              onClick={() => { setActive(l.code); setOpen(false); setLang(l.code); }}
              data-testid={`language-switcher-option-${l.code}`}
              className={`block w-full text-left px-3 py-2 text-sm transition-colors ${l.code === active ? "bg-[#FF3B30] text-white" : "text-black hover:bg-black hover:text-white"}`}
            >
              {l.label}
              {l.code === "en" && <span className="text-[9px] opacity-60 ml-1">(default)</span>}
            </button>
          ))}
        </div>
      )}
      {/* Hide Google's intrusive top banner — we expose our own UI */}
      <style>{`
        .goog-te-banner-frame, #goog-gt-tt, .goog-tooltip { display: none !important; }
        body { top: 0px !important; }
        .skiptranslate iframe { display: none !important; }
      `}</style>
    </div>
  );
}
